import React, { useState } from "react";
import { Box, Button } from "@material-ui/core";
import { HFlex, ProgressButton, Text, DialogSuccessNotice, useErrorContext } from "shared";
import { FormProvider, useForm } from "react-hook-form";
import { useUpdateHcpService } from "api/hooks/useHcpServices";
import { ServiceItem } from "routes/hcp/types";
import ServiceFormFields, { ServiceFormData, formValuesToPayload, serviceToFormValues } from "./ServiceFormFields";

interface Props {
  hcpId: number;
  service: ServiceItem;
  onSuccess: () => void;
  onCancel: () => void;
}

const EditServiceForm: React.VFC<Props> = ({ hcpId, service, onSuccess, onCancel }) => {
  const setError = useErrorContext();
  const [successData, setSuccessData] = useState<{ heading: string; body: string } | null>(null);

  const form = useForm<ServiceFormData>({ defaultValues: serviceToFormValues(service) });

  const updateServiceMutation = useUpdateHcpService();

  const submit = form.handleSubmit(async fields => {
    try {
      const updated = await updateServiceMutation.mutateAsync({
        hcpId,
        serviceId: service.id,
        ...formValuesToPayload(fields),
        health_care_provider_service_category_id: service.category?.id,
      });

      setSuccessData({
        heading: "Service updated successfully",
        body: `${updated.name} (ID: ${updated.id})`,
      });
    } catch (err) {
      setError("Could not update service", err);
    }
  });

  if (successData)
    return <DialogSuccessNotice heading={successData.heading} body={successData.body} onClose={() => onSuccess()} />;

  return (
    <Box mx={3}>
      <Text heading bold mb={3}>
        Edit Service
      </Text>
      <FormProvider {...form}>
        <form onSubmit={submit}>
          <ServiceFormFields
            hcpId={hcpId}
            paymentMethodsDefault={service.payment_methods ?? []}
            professionalIdsDefault={service.health_care_professional_ids ?? []}
          />

          <HFlex justifyContent="flex-end" gap={2} my={4}>
            <Button disabled={updateServiceMutation.isLoading} onClick={onCancel}>
              Cancel
            </Button>
            <ProgressButton loading={updateServiceMutation.isLoading}>Save</ProgressButton>
          </HFlex>
        </form>
      </FormProvider>
    </Box>
  );
};

export default EditServiceForm;
